import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import {
  DeriveTrackerInput,
  TRACKER_STEP_ORDER,
  buildMinimalTracker,
  buildTrackerSteps,
} from "./trackerSteps";
import { TRACKER_STEP_ICONS } from "./trackerIcons";
import {
  DetailedCollapsedRow,
  TimelineConnector,
  TimelineDate,
  TimelineIconColumn,
  TimelineLabelWrapper,
  TimelineList,
  TimelineRow,
  TrackerChevron,
  TrackerContainer,
  TrackerDate,
  TrackerHeader,
  TrackerHeaderLabel,
  TrackerIcon,
  TrackerLabel,
  TrackerSubtext,
  TrackerTitle,
} from "./OrderStatusTracker.styles";

interface DetailedTrackerProps {
  input: DeriveTrackerInput;
  showEstimatedDate: boolean;
  /** Section heading shown above the tracker (e.g. "Aneuploidy Screen"). */
  title: string;
  /** Initial expanded state; defaults to expanded (the point of the detailed view). */
  defaultExpanded?: boolean;
}

/**
 * Detailed "Order status" tracker: a collapsible vertical timeline of all four
 * steps. Completed/current steps are emphasized in purple; upcoming steps are
 * greyed. Collapsing shows only the current step.
 */
export const DetailedTracker: React.FC<DetailedTrackerProps> = ({
  input,
  showEstimatedDate,
  title,
  defaultExpanded = true,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const steps = buildTrackerSteps(input, showEstimatedDate);
  const collapsedView = buildMinimalTracker(input, showEstimatedDate);

  return (
    <TrackerContainer data-testid="detailed-tracker">
      <TrackerTitle>{title}</TrackerTitle>
      <TrackerHeader
        type="button"
        aria-expanded={expanded}
        aria-label="Toggle order status details"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <TrackerHeaderLabel>Order status</TrackerHeaderLabel>
        <TrackerChevron>
          <FontAwesomeIcon
            aria-hidden
            icon={expanded ? faChevronUp : faChevronDown}
          />
        </TrackerChevron>
      </TrackerHeader>

      {expanded ? (
        <TimelineList>
          {steps.map((step, index) => {
            const isActive = step.state !== "upcoming";
            const isLast = index === TRACKER_STEP_ORDER.length - 1;
            return (
              <TimelineRow key={step.id}>
                <TimelineDate visible={Boolean(step.date)}>
                  {step.date}
                </TimelineDate>
                <TimelineIconColumn>
                  <TrackerIcon active={isActive}>
                    <FontAwesomeIcon
                      aria-hidden
                      icon={TRACKER_STEP_ICONS[step.id]}
                    />
                  </TrackerIcon>
                  {!isLast && <TimelineConnector state={step.state} />}
                </TimelineIconColumn>
                <TimelineLabelWrapper>
                  <TrackerLabel active={isActive}>{step.label}</TrackerLabel>
                  {step.subtext && (
                    <TrackerSubtext>{step.subtext}</TrackerSubtext>
                  )}
                </TimelineLabelWrapper>
              </TimelineRow>
            );
          })}
        </TimelineList>
      ) : (
        <DetailedCollapsedRow>
          <TrackerDate>{collapsedView.date}</TrackerDate>
          <TrackerIcon active>
            <FontAwesomeIcon
              aria-hidden
              icon={TRACKER_STEP_ICONS[collapsedView.currentStepId]}
            />
          </TrackerIcon>
          <span>
            <TrackerLabel active>{collapsedView.label}</TrackerLabel>
            {collapsedView.subtext && (
              <TrackerSubtext>{collapsedView.subtext}</TrackerSubtext>
            )}
          </span>
        </DetailedCollapsedRow>
      )}
    </TrackerContainer>
  );
};
